import { useEffect, useMemo, useState } from "react";
import { toast } from "sonner";
import { Loader2 } from "lucide-react";
import { getAccountsSummary } from "@/lib/api";
import {
  CsvButton, Empty, SortTh, TH, TR, downloadCsv, errMsg, money, pct, useSort,
} from "@/components/accounts/shared";

// One gym per row, each in its own currency; subtotals per currency (INR, LKR, AED are never added).
const COLS = [
  ["booked", "Sales booked", "Sold in the period: consult's amount sold or the sale price. Incl. tax."],
  ["collected", "Collected", "Payments received in the period (a balance counts the day it's paid). Incl. tax, before gateway fees."],
  ["refunds", "Refunds", "Refunds entered in the period. Incl. tax."],
  ["tax", "Tax", "GST / VAT inside the money collected, less the tax share of refunds (= Dashboard GST)."],
  ["fees", "Gateway fees", "Card / gateway fees on payments in the period."],
  ["expenses", "Expenses", "Expenses dated in the period, incl. the monthly capex depreciation (= Reports total expenses)."],
  ["net_after_expenses", "Net", "Collected − refunds − expenses. Cash basis, incl. tax."],
  ["outstanding", "Dues outstanding", "Balances still owed today on every open sale, any sale date. Incl. tax."],
];

export default function AccountsOverview({ month, country, gym }) {
  const [data, setData] = useState(null);
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    let live = true;
    setBusy(true);
    getAccountsSummary({ month, country: country || undefined, facility_id: gym || undefined })
      .then((d) => live && setData(d))
      .catch((e) => toast.error(errMsg(e, "Could not load the accounts summary")))
      .finally(() => live && setBusy(false));
    return () => { live = false; };
  }, [month, country, gym]);

  const byCurrency = useMemo(() => {
    const out = {};
    (data?.gyms || []).forEach((g) => { (out[g.currency_code] = out[g.currency_code] || []).push(g); });
    return out;
  }, [data]);

  if (!data) return <div className="py-16 flex justify-center"><Loader2 className="h-5 w-5 animate-spin text-zinc-500" /></div>;

  return (
    <div className="space-y-6" data-testid="accounts-overview">
      <div className="text-xs text-zinc-500">
        {data.period.label} · compared with {data.previous.label}. Amounts are <span className="text-zinc-300">incl. tax</span> unless
        a column says otherwise; each gym in its own currency. {busy && <Loader2 className="inline h-3 w-3 animate-spin ml-1" />}
      </div>

      {data.gyms.length === 0 && <div className="card-surface"><Empty>No active gyms match these filters.</Empty></div>}

      {data.subtotals.map((sub) => (
        <div key={sub.currency_code} className="card-surface overflow-x-auto" data-testid={`accounts-currency-${sub.currency_code}`}>
          <div className="px-4 pt-3 pb-2 flex flex-wrap items-baseline justify-between gap-2">
            <div>
              <span className="text-sm font-semibold text-zinc-100">{sub.currency_code}</span>
              <span className="text-xs text-zinc-500 ml-2">{sub.countries.join(", ")} · {sub.gyms} gym{sub.gyms === 1 ? "" : "s"}</span>
            </div>
            <div className="text-xs text-zinc-400">
              Collected {money(sub, sub.collected)} <span className={sub.collected_change >= 0 ? "text-emerald-400" : "text-rose-400"}>{pct(sub.collected_change)}</span>
              <span className="text-zinc-600"> vs {money(sub, sub.prev_collected)}</span>
              <span className="mx-2 text-zinc-700">|</span>
              Expenses {money(sub, sub.expenses)} <span className={sub.expenses_change > 0 ? "text-rose-400" : "text-emerald-400"}>{pct(sub.expenses_change)}</span>
              <span className="text-zinc-600"> vs {money(sub, sub.prev_expenses)}</span>
            </div>
          </div>
          <table className="w-full text-sm min-w-[980px]">
            <thead>
              <tr className={TH}>
                <th className="px-3 py-2 font-medium">Gym</th>
                {COLS.map(([k, label, hint]) => <th key={k} className="px-3 py-2 font-medium text-right" title={hint}>{label}</th>)}
                <th className="px-3 py-2 font-medium text-right" title="Collected this period vs the previous one">Collected vs prev.</th>
              </tr>
            </thead>
            <tbody>
              {(byCurrency[sub.currency_code] || []).map((g) => (
                <tr key={g.facility_id} className={TR} data-testid={`accounts-gym-${g.facility_id}`}>
                  <td className="px-3 py-2">
                    <div className="font-medium text-zinc-200">{g.gym}</div>
                    <div className="text-[10px] text-zinc-500">{g.sales_count} sale{g.sales_count === 1 ? "" : "s"} · {g.tax_enabled ? `${g.tax_name} ${Math.round(g.tax_rate * 100)}%` : "no tax"}</div>
                  </td>
                  {COLS.map(([k]) => (
                    <td key={k} className={`px-3 py-2 text-right tabular-nums ${k === "net_after_expenses" ? (g[k] < 0 ? "text-rose-300" : "text-emerald-300") : "text-zinc-300"}`}>
                      {money(g, g[k])}
                      {k === "expenses" && g.depreciation > 0 && <div className="text-[10px] text-zinc-500">incl. {money(g, g.depreciation)} depreciation</div>}
                      {k === "outstanding" && g.outstanding_count > 0 && <div className="text-[10px] text-zinc-500">{g.outstanding_count} open</div>}
                    </td>
                  ))}
                  <td className="px-3 py-2 text-right text-xs">
                    <span className={g.collected_change >= 0 ? "text-emerald-400" : "text-rose-400"}>{pct(g.collected_change)}</span>
                    <div className="text-[10px] text-zinc-500">{money(g, g.prev_collected)}</div>
                  </td>
                </tr>
              ))}
              <tr className="bg-[#101010] font-semibold">
                <td className="px-3 py-2 text-zinc-300">Subtotal {sub.currency_code}</td>
                {COLS.map(([k]) => <td key={k} className="px-3 py-2 text-right tabular-nums text-zinc-100">{money(sub, sub[k])}</td>)}
                <td className="px-3 py-2 text-right text-xs text-zinc-400">{pct(sub.collected_change)}</td>
              </tr>
            </tbody>
          </table>
          <div className="px-4 py-2 text-[11px] text-zinc-500 border-t border-[#1a1a1a]">
            Ex-tax, ex-fee revenue (Reports "Total revenue"): {money(sub, sub.net_revenue)} · Profit (revenue − expenses): {money(sub, sub.profit)}
          </div>
        </div>
      ))}

      <PendingList rows={data.pending} />
      <RefundList rows={data.refunds} label={data.period.label} />
    </div>
  );
}

const PENDING_COLS = {
  member: (r) => r.member, gym: (r) => r.gym, sale_date: (r) => r.sale_date || "", programme: (r) => r.programme || "",
  amount_due: (r) => r.amount_due, days: (r) => r.days_outstanding ?? -1,
};

function PendingList({ rows }) {
  const sort = useSort(rows, PENDING_COLS, "days");
  const csv = () => downloadCsv("pending-payments.csv",
    ["Member", "Phone", "Gym", "Currency", "Sale date", "Programme", "Sold for", "Paid", "Amount due", "Due date", "Days outstanding"],
    sort.sorted.map((r) => [r.member, r.phone, r.gym, r.currency_code, r.sale_date, r.programme, r.booked, r.paid, r.amount_due, r.due_date, r.days_outstanding]));
  return (
    <div className="card-surface overflow-x-auto" data-testid="accounts-pending">
      <div className="px-4 py-3 flex items-center justify-between gap-2">
        <div>
          <div className="text-sm font-semibold text-zinc-100">Pending payments</div>
          <div className="text-[11px] text-zinc-500">Every open balance today, any sale date. Amounts incl. tax.</div>
        </div>
        <CsvButton onClick={csv} disabled={!rows.length} testid="accounts-pending-csv" />
      </div>
      {rows.length === 0 ? <Empty>Nobody owes anything.</Empty> : (
        <table className="w-full text-sm min-w-[760px]">
          <thead>
            <tr className={TH}>
              <SortTh sort={sort} k="member">Member</SortTh>
              <SortTh sort={sort} k="gym">Gym</SortTh>
              <SortTh sort={sort} k="sale_date">Sale date</SortTh>
              <SortTh sort={sort} k="programme">Programme</SortTh>
              <SortTh sort={sort} k="amount_due" right>Amount due</SortTh>
              <SortTh sort={sort} k="days" right>Days outstanding</SortTh>
            </tr>
          </thead>
          <tbody>
            {sort.sorted.map((r) => (
              <tr key={r.sale_id} className={TR}>
                <td className="px-3 py-2 text-zinc-200">{r.member}<div className="text-[10px] text-zinc-500">{r.phone}</div></td>
                <td className="px-3 py-2 text-zinc-400">{r.gym}</td>
                <td className="px-3 py-2 text-zinc-400 tabular-nums">{r.sale_date}</td>
                <td className="px-3 py-2 text-zinc-400">{r.programme}</td>
                <td className="px-3 py-2 text-right tabular-nums text-amber-200">{money(r, r.amount_due)}
                  <div className="text-[10px] text-zinc-500">of {money(r, r.booked)}</div></td>
                <td className={`px-3 py-2 text-right tabular-nums ${r.days_outstanding > 30 ? "text-rose-300" : "text-zinc-300"}`}>{r.days_outstanding ?? "—"}
                  {r.due_date && <div className="text-[10px] text-zinc-500">due {r.due_date}</div>}</td>
              </tr>
            ))}
          </tbody>
        </table>
      )}
    </div>
  );
}

function RefundList({ rows, label }) {
  const csv = () => downloadCsv("refunds.csv", ["Date", "Member", "Gym", "Currency", "Programme", "Sale date", "Amount", "Tax share"],
    rows.map((r) => [r.date, r.member, r.gym, r.currency_code, r.programme, r.sale_date, r.amount, r.tax]));
  return (
    <div className="card-surface overflow-x-auto" data-testid="accounts-refunds">
      <div className="px-4 py-3 flex items-center justify-between gap-2">
        <div>
          <div className="text-sm font-semibold text-zinc-100">Refunds · {label}</div>
          <div className="text-[11px] text-zinc-500">Dated the day the refund was entered. Amounts incl. tax.</div>
        </div>
        <CsvButton onClick={csv} disabled={!rows.length} testid="accounts-refunds-csv" />
      </div>
      {rows.length === 0 ? <Empty>No refunds in this period.</Empty> : (
        <table className="w-full text-sm min-w-[640px]">
          <thead><tr className={TH}>
            <th className="px-3 py-2 font-medium">Date</th><th className="px-3 py-2 font-medium">Member</th>
            <th className="px-3 py-2 font-medium">Gym</th><th className="px-3 py-2 font-medium">Programme</th>
            <th className="px-3 py-2 font-medium text-right">Amount</th>
          </tr></thead>
          <tbody>
            {rows.map((r) => (
              <tr key={r.refund_id} className={TR}>
                <td className="px-3 py-2 tabular-nums text-zinc-400">{r.date}</td>
                <td className="px-3 py-2 text-zinc-200">{r.member}<div className="text-[10px] text-zinc-500">sold {r.sale_date}</div></td>
                <td className="px-3 py-2 text-zinc-400">{r.gym}</td>
                <td className="px-3 py-2 text-zinc-400">{r.programme}</td>
                <td className="px-3 py-2 text-right tabular-nums text-rose-200">{money(r, r.amount)}
                  {r.tax > 0 && <div className="text-[10px] text-zinc-500">incl. {money(r, r.tax, true)} tax</div>}</td>
              </tr>
            ))}
          </tbody>
        </table>
      )}
    </div>
  );
}
